import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    organization_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    setError("");
    const r = await register(form);
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      toast.error(r.error);
      return;
    }
    toast.success("Workspace created");
    navigate("/app", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between bg-[#0A1128] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="relative">
          <Link to="/" className="flex items-center">
            <Logo onDark className="h-8" />
          </Link>
        </div>
        <div className="relative space-y-6">
          <h2 className="text-3xl font-bold tracking-tight">
            Create your workspace in under a minute.
          </h2>
          <ul className="space-y-3 text-sm text-white/80">
            <li>· Upload your first document immediately</li>
            <li>· Invite your team with role-based access</li>
            <li>· Every agreement is audit-ready by default</li>
          </ul>
        </div>
      </div>

      <div className="flex flex-col justify-center p-6 sm:p-12">
        <div className="max-w-md w-full mx-auto">
          <h1 className="text-3xl font-bold tracking-tight">Create your workspace</h1>
          <p className="mt-2 text-sm text-slate-500">
            You'll be the organization admin. Invite teammates once you're in.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
            <div>
              <Label className="text-sm font-medium text-slate-700">Full name</Label>
              <Input
                required
                value={form.name}
                onChange={set("name")}
                className="mt-1.5 h-11"
                placeholder="Priya Sharma"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Work email</Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                className="mt-1.5 h-11"
                placeholder="priya@company.com"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Password</Label>
              <PasswordInput
                required
                value={form.password}
                onChange={set("password")}
                className="mt-1.5 h-11"
                placeholder="At least 6 characters"
                data-testid="register-password-input"
                testIdToggle="register-password-toggle"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Organization name</Label>
              <Input
                value={form.organization_name}
                onChange={set("organization_name")}
                className="mt-1.5 h-11"
                placeholder="Acme Inc."
                data-testid="register-org-input"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating…" : "Create workspace"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-500 text-center">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-slate-900 hover:text-emerald-700">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
